import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LicenseBadge } from '@/components/fintech/LicenseBadge';
import { ShieldCheck, Landmark, ArrowRightLeft, Building2, Briefcase, Wallet, ArrowRight, Sparkles } from 'lucide-react';

export default function LandingPage() {
  const { t } = useLanguage();
  const { company } = useOutletContext() || {};

  const services = t('landing.services') || [];
  const howSteps = t('landing.how_steps') || [];
  const whyList = t('landing.why') || [];

  const icons = [Wallet, ArrowRightLeft, Building2, Briefcase, Landmark, ShieldCheck];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="noise absolute inset-0" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--brand-navy))]/15 bg-white/60 px-3 py-1 text-xs text-[hsl(var(--brand-navy))]">
                <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--brand-gold))]" />
                {t('landing.trust_headline')}
              </div>
              <h1 className="font-serif mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[hsl(var(--brand-navy-2))]">
                {t('landing.hero_title')}
              </h1>
              <p className="mt-6 text-base sm:text-lg leading-7 text-[hsl(var(--muted-foreground))] max-w-2xl">
                {t('landing.hero_subtitle')}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button asChild size="lg" data-testid="landing-hero-primary-cta" className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))] text-white">
                  <Link to="/register">{t('landing.hero_cta_primary')} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" data-testid="landing-hero-secondary-cta">
                  <Link to="/documents">{t('landing.hero_cta_secondary')}</Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <Card className="relative overflow-hidden bg-white border rounded-2xl">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[hsl(var(--brand-gold))] via-[hsl(var(--brand-gold))]/40 to-transparent" />
                <CardContent className="p-6">
                  <img src="/logo.png" alt="Concorde Financial Limited" className="h-12 w-auto mb-3" />
                  <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Regulatory</div>
                  <div className="font-serif text-lg mt-2 text-[hsl(var(--brand-navy-2))]">{company?.name || 'Concorde Financial Limited'}</div>
                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm">
                    <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">License</span><span className="font-semibold">{company?.license || 'L16195/CFL'}</span></div>
                    <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">Regulator</span><span className="font-semibold text-right max-w-[60%]">{company?.regulator || 'Anjouan Offshore Finance Authority'}</span></div>
                    <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">Jurisdiction</span><span className="font-semibold text-right max-w-[60%]">{company?.jurisdiction || 'Union of Comoros'}</span></div>
                    <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">Company N°</span><span className="font-semibold">{company?.number || '16195'}</span></div>
                    <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">Valid until</span><span className="font-semibold">{company?.license_valid_until || '2026-11-03'}</span></div>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {(company?.supported_currencies || ['USD', 'EUR', 'KMF', 'KZT']).map(c => (
                      <span key={c} className="rounded-full border bg-[hsl(var(--secondary))] px-2.5 py-0.5 text-xs font-medium">{c}</span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--brand-gold))]">Services</div>
            <h2 className="font-serif text-3xl sm:text-4xl mt-2 text-[hsl(var(--brand-navy-2))]">{t('landing.services_title')}</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((s, i) => {
              const Icon = icons[i] || Wallet;
              return (
                <Card key={i} className="bg-white hover:shadow-[0_12px_30px_-18px_hsl(var(--brand-shadow)/0.25)] transition-shadow">
                  <CardContent className="p-6">
                    <div className="h-10 w-10 rounded-xl bg-[hsl(var(--brand-gold))]/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-[hsl(var(--brand-navy))]" />
                    </div>
                    <div className="mt-4 font-serif text-xl text-[hsl(var(--brand-navy-2))]">{s.title}</div>
                    <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-6">{s.body}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 lg:py-20 bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--brand-gold))]">Process</div>
          <h2 className="font-serif text-3xl sm:text-4xl mt-2 text-[hsl(var(--brand-navy-2))]">{t('landing.how_title')}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {howSteps.map((s, i) => (
              <div key={i} className="rounded-2xl border bg-white p-6">
                <div className="font-serif text-2xl text-[hsl(var(--brand-gold))]">0{i + 1}</div>
                <div className="font-semibold mt-3">{s.title}</div>
                <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-6">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--brand-gold))]">Trust</div>
          <h2 className="font-serif text-3xl sm:text-4xl mt-2 text-[hsl(var(--brand-navy-2))]">{t('landing.why_title')}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {whyList.map((w, i) => (
              <div key={i} className="rounded-2xl border bg-white p-6">
                <div className="h-8 w-8 rounded-lg bg-[hsl(var(--brand-navy))] flex items-center justify-center">
                  <ShieldCheck className="h-4 w-4 text-[hsl(var(--brand-gold))]" />
                </div>
                <div className="mt-4 font-semibold">{w.title}</div>
                <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-6">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[hsl(var(--brand-navy-2))] text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl">{t('landing.cta_bottom_title')}</h2>
          <p className="mt-3 text-white/70">{t('landing.cta_bottom_subtitle')}</p>
          <div className="mt-8 flex justify-center gap-3">
            <Button asChild size="lg" className="bg-[hsl(var(--brand-gold))] hover:bg-[hsl(43_74%_46%)] text-[hsl(var(--brand-ink))]" data-testid="landing-cta-bottom-button">
              <Link to="/register">{t('landing.cta_bottom_button')} <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="mt-6 flex justify-center"><LicenseBadge /></div>
        </div>
      </section>
    </div>
  );
}
